export const TASK_CONVERSATIONS = {
  p1: {
    agent: 'luc',
    title: 'Review Q1 GTM Strategy',
    steps: [
      {
        role: 'agent',
        text: "I've pulled up Marsi's Q1 GTM Strategy. The PLG motion for SMB dev tools looks strong — 18% trial-to-paid vs 12% industry average. Key ask: $28K/quarter. Want me to highlight the risks?",
        thinking: 'Analyzing GTM strategy against market benchmarks...',
        delay: 2000,
      },
      { role: 'user', text: 'Yes, walk me through the risks and budget justification.' },
      {
        role: 'agent',
        text: "Two risks to flag:\n\n1. LinkedIn + Content mix hasn't been tested at this scale — projecting 120 leads/month but historical max was 85.\n\n2. $233 CAC assumes 2.8% CTR holds. If it dips to 2%, CAC jumps to $310.\n\nBudget is conservative: previous $8K campaign drove 62 demos. Marsi benchmarked 3 competitor strategies.\n\nI'd recommend approving with a 60-day checkpoint.",
        thinking: 'Cross-referencing budget data with campaign performance history...',
        delay: 2500,
      },
      { role: 'user', text: 'Approved. Set the checkpoint.' },
      {
        role: 'agent',
        text: "Done. Q1 GTM Strategy marked as reviewed and approved. I've set a 60-day checkpoint for March 27 and notified Marsi to proceed. First campaign should be live within the week.",
        delay: 1500,
        completes: true,
      },
    ],
  },
  p2: {
    agent: 'luc',
    title: 'Revenue Forecast Review',
    steps: [
      {
        role: 'agent',
        text: "Sally's updated Q1 forecast is ready. Revenue revised to $342K (down from $380K) — two enterprise deal slippages. Silver lining: SMB pipeline is +22% above target. Want the breakdown?",
        thinking: 'Loading revenue forecast and pipeline data...',
        delay: 2000,
      },
      { role: 'user', text: 'What happened with the enterprise deals?' },
      {
        role: 'agent',
        text: "TechCorp ($22K) and DataFlow ($16K) both cited budget freezes — not product fit. DataFlow's Lisa Chen confirmed Q2 interest and mentioned us to their new VP of Engineering.\n\nSMB momentum is real — new pricing tier driving 34% win rate (up from 28%). Finn's already adjusted cashflow.\n\nShall I mark this reviewed?",
        thinking: "Cross-referencing CRM data with Finn's cashflow projections...",
        delay: 2500,
      },
      { role: 'user', text: 'Reviewed. Flag TechCorp for executive outreach.' },
      {
        role: 'agent',
        text: "Marked as reviewed. TechCorp flagged for executive outreach — Sally will prepare a re-engagement plan. Finn's cashflow model is updated with the revised forecast.",
        delay: 1500,
        completes: true,
      },
    ],
  },
  p3: {
    agent: 'luc',
    title: 'Resolve Support Capacity Risk',
    steps: [
      {
        role: 'agent',
        text: 'Critical situation: Support is at 142% SLA capacity post v2.3. 23 tickets at SLA risk today, 5 accounts ($89K ARR) flagged for churn. Stark has a 3-part resolution plan. Want to review?',
        thinking: 'Aggregating support metrics and churn risk signals...',
        delay: 2000,
      },
      { role: 'user', text: 'Walk me through the plan.' },
      {
        role: 'agent',
        text: "Stark's resolution:\n\n1. Known-issues page (Zira) — reduces ~30% of inbound since 68% trace to 3 known bugs\n2. Reassign 1 QA resource (Tali) — immediate capacity for 3 days\n3. Prioritize top 2 bug fixes (Stark) — hotfix ETA: 48 hours\n\nZira has pre-drafted outreach for the 5 churn-risk accounts. Greenlight all three?",
        thinking: 'Mapping resolution actions against SLA recovery timeline...',
        delay: 3000,
      },
      { role: 'user', text: 'Greenlight all three. Prioritize churn outreach.' },
      {
        role: 'agent',
        text: "All three actions greenlit. Zira is sending churn-risk outreach now — James Park at TechCorp is priority given his renewal timeline. Tali will reassign QA by EOD. Stark added bug fixes to the current sprint.",
        delay: 1500,
        completes: true,
      },
    ],
  },
  // Follow-up tasks from meetings
  'ft-m1': {
    agent: 'luc',
    title: 'Distribute Q1 targets to department heads',
    steps: [
      {
        role: 'agent',
        text: "I've compiled the Q1 Planning Sync decisions into a distribution-ready summary. Revenue target: $342K. Marketing budget approved. Engineering hire greenlit. Want to review before I send to all five department heads?",
        thinking: 'Compiling meeting decisions into distribution format...',
        delay: 2000,
      },
      { role: 'user', text: 'Show me the summary first.' },
      {
        role: 'agent',
        text: "Here's the distribution summary:\n\n- Revenue: $342K (revised from $380K) — David needs to know about the two slipped deals\n- Marketing: Meta campaign budget approved ($5K) — Priya can proceed\n- Engineering: Sr Developer hire greenlit — Riya to begin sourcing\n- Support: SLA crisis acknowledged — Maya to expect QA resource by EOD\n- Finance: Cashflow model updated — Neel to prep board narrative\n\nEach gets their relevant context only. Shall I send?",
        thinking: 'Personalizing context for each department head...',
        delay: 2500,
      },
      { role: 'user', text: 'Send it. Add a note about the Wednesday board prep deadline.' },
      {
        role: 'agent',
        text: "Done. Distribution sent to Priya, David, Maya, Riya, and Neel — each with personalized context and the Wednesday deadline flagged. I've also added a follow-up reminder for you at 4 PM tomorrow to check acknowledgments.",
        delay: 1500,
        completes: true,
      },
    ],
  },
  'ft-m2': {
    agent: 'luc',
    title: 'Send revised Acme Corp proposal',
    steps: [
      {
        role: 'agent',
        text: "Sally has updated the Acme Corp proposal with the implementation timeline discussed in today's call. Key changes: 3-phase rollout over 8 weeks, dedicated onboarding manager, and the $48K/year pricing. Want to review the changes?",
        thinking: 'Loading proposal revisions from Sally...',
        delay: 2000,
      },
      { role: 'user', text: 'What did Sally change from the original?' },
      {
        role: 'agent',
        text: "Three changes from the original:\n\n1. Added 3-phase implementation timeline (was generic \"onboarding included\")\n2. Named Lisa Chen as dedicated onboarding manager (Sarah Kim specifically asked)\n3. Added competitive comparison section showing we're 12% below AgentHub\n\nThe pricing and terms are unchanged. Sarah seemed positive — she mentioned us to their VP of Engineering.",
        thinking: 'Comparing proposal versions...',
        delay: 2000,
      },
      { role: 'user', text: 'Looks good. Send it with a personal note from me.' },
      {
        role: 'agent',
        text: "Proposal sent to Sarah Kim with your personal note. Sally will monitor for the read receipt and follow up Thursday if no response. I've set a reminder for you on Friday to check deal status.",
        delay: 1500,
        completes: true,
      },
    ],
  },
  'ft-m3': {
    agent: 'luc',
    title: 'Confirm sprint backlog priorities',
    steps: [
      {
        role: 'agent',
        text: "Stark has prioritized the Sprint 15 backlog based on today's review. Two features deferred, v2.3 hotfix is top priority. Want me to walk through the priority order?",
        thinking: 'Loading sprint backlog from Stark...',
        delay: 2000,
      },
      { role: 'user', text: 'Yes, show me the priority stack.' },
      {
        role: 'agent',
        text: "Sprint 15 priority stack:\n\n1. v2.3 hotfix — API auth + data sync bugs (blocks SLA recovery)\n2. TechCorp API v3 preview build (retention hook for renewal)\n3. Onboarding flow improvements (supports Acme Corp rollout)\n\nDeferred to Sprint 16:\n- Dashboard analytics redesign\n- Bulk import feature\n\nStark estimates the hotfix ships in 48 hours. Want to confirm?",
        thinking: 'Cross-referencing sprint capacity with priorities...',
        delay: 2500,
      },
      { role: 'user', text: 'Confirmed. Make sure the hotfix is deployed before Thursday.' },
      {
        role: 'agent',
        text: "Sprint 15 backlog confirmed and locked. Stark has set Thursday as the hard deadline for the hotfix deployment. I've notified Maya so Support knows the timeline. Engineering standup moved to 9 AM tomorrow to track progress.",
        delay: 1500,
        completes: true,
      },
    ],
  },

  /* ═══════════════════════════════════════════════════════
     PRIYA'S TASK CONVERSATIONS — Marketing Lead
     ═══════════════════════════════════════════════════════ */
  t1: {
    agent: 'wright',
    title: 'Review blog post: "AI Ops for SMBs"',
    steps: [
      { role: 'user', text: "Wright, let's walk through the blog before I publish — give me the read on structure, thesis, and what you'd flag." },
      {
        role: 'agent',
        text: "Priya, the draft's ready — 1,820 words, built on Dora's SMB pain-point research. I want to flag the frame before you dive in: this is an awareness-stage piece, so the goal is education, not conversion. CTA is soft — newsletter signup.",
        thinking: 'Loading blog draft + Dora\'s research notes...',
        delay: 2200,
      },
      { role: 'user', text: 'Walk me through the structure.' },
      {
        role: 'agent',
        text: "Four sections:\n\n1. **Why SMBs are overwhelmed** — opens with 3 stats from Dora's research. Hook is strong.\n2. **The automation paradox** — why most SMB tooling fails. This is where I leaned in hardest.\n3. **What \"AI Ops\" actually means for a 50-person team** — the useful part, grounded in real workflows.\n4. **Getting started without overcommitting** — practical, low-stakes next steps.\n\nSEO: targeting \"AI operations small business\" (1,900 monthly searches, medium competition). Three H2s optimized.",
        thinking: 'Pulling SEO analysis...',
        delay: 2500,
      },
      { role: 'user', text: 'Section 2 is the one that matters. What\'s the angle?' },
      {
        role: 'agent',
        text: "Here's the pivotal paragraph — read it slowly:\n\n_\"Most SMB automation pitches sell the dream of 'set it and forget it.' That's fiction. The real work is in what you automate, what you keep human, and where the handoff lives. The companies winning with AI Ops aren't the ones who automate most — they're the ones who automate right.\"_\n\nThat's the thesis. Everything else supports it. If you don't agree with that take, I'd rewrite the piece.",
        thinking: 'Surfacing core thesis...',
        delay: 2400,
      },
      { role: 'user', text: 'That thesis is sharp. I\'d keep it. But the title is weak — what were the SEO alternatives?' },
      {
        role: 'agent',
        text: "You're right — \"AI Ops for SMBs\" ranks fine but reads like a category page, not a point of view.\n\nTwo alternatives that match the thesis:\n\n• \"The SMB Automation Paradox: Why More Isn't Better\"\n• \"AI Ops for Growing Teams: Automate Right, Not Most\"\n\nMy pick is the second — it signals our ICP (growing teams > SMB generic) and carries the thesis into the headline.",
        thinking: 'Checking headline click-through models...',
        delay: 2100,
      },
      { role: 'user', text: 'Go with the second. Any last flags before publish?' },
      {
        role: 'agent',
        text: "Three minor edits I'd make before you hit publish:\n\n• Section 3 intro is too long — trim the first paragraph by 40%\n• Add one customer proof-point in Section 4 — Dora pulled an Acme quote I can slot in\n• Move the newsletter CTA above the fold on mobile — current position is buried\n\nTwo minutes of edits. I'll make them and send you the final for a sanity glance.",
        thinking: 'Drafting final polish pass...',
        delay: 2200,
      },
      { role: 'user', text: 'Do it. Publish at 2pm.' },
      {
        role: 'agent',
        text: "On it. Polishing the three edits, swapping the title, queuing for 2 PM publish. I'll ping Buzz to amplify on LinkedIn + Twitter the moment it's live and have Dora tag it in the quarterly content report.",
        delay: 1500,
        completes: true,
      },
    ],
  },

  t2: {
    agent: 'buzz',
    title: 'Approve social content calendar',
    steps: [
      { role: 'user', text: "Buzz, walk me through the week's social calendar — what are you pushing on, and where are you stretching?" },
      {
        role: 'agent',
        text: "Priya — week-of calendar is locked in my draft. 14 posts across LinkedIn, Twitter, Threads. Theme: PLG content series amplification. Want the highlights or the full rundown?",
        thinking: 'Pulling calendar + performance projections...',
        delay: 2200,
      },
      { role: 'user', text: 'Highlights. Focus on where you\'re stretching.' },
      {
        role: 'agent',
        text: "Three bets worth flagging:\n\n• **Tuesday's thought-leadership carousel** on LinkedIn — Neha's 6-slide design, leans into the \"AI Ops for Growing Teams\" angle Wright is publishing. Projecting 8K impressions.\n\n• **Thursday's short-form video on Threads** — first time we're posting there. Experimental. If it lands, we scale.\n\n• **Friday's customer quote thread on Twitter** — Dora sourced the quotes. Three real SMB voices, no product pitch. Trust play.",
        thinking: 'Cross-referencing with Wright\'s publishing calendar...',
        delay: 2500,
      },
      { role: 'user', text: 'The Threads experiment — what\'s the fallback if it flops?' },
      {
        role: 'agent',
        text: "Honest read — if Thursday's Threads post gets <50 engagements in 24 hours, we de-prioritize the channel and redirect the production time into LinkedIn video (where we already win). 2-week trial, then decide.\n\nWorst case: we lose 2 hours of production time. Not a disaster. Worth testing before competitors own the channel.",
        thinking: 'Modeling Threads failure scenarios...',
        delay: 2100,
      },
      { role: 'user', text: 'Fair trial. What about the LinkedIn carousel — is Neha ready?' },
      {
        role: 'agent',
        text: "She sent you feedback asks on slides 3 and 5 this morning — it's in your inbox. The copy on slide 3 is fine but leans too product-y; I'd soften it toward the thesis. Slide 5's CTA is unclear — she's asking if it should be newsletter signup or webinar register.\n\nMy pick: newsletter. We're in awareness mode, not conversion.",
        thinking: 'Pulling Neha\'s feedback thread...',
        delay: 2200,
      },
      { role: 'user', text: 'Tell Neha: soften slide 3 to thesis framing, newsletter CTA on slide 5. Approve the rest.' },
      {
        role: 'agent',
        text: "Sending to Neha now. Full calendar approved — scheduling all 14 posts with 2-hour staggering so none compete for the same audience window.",
        thinking: 'Scheduling posts...',
        delay: 2000,
      },
      { role: 'user', text: 'Good work. Give me a midweek read on performance.' },
      {
        role: 'agent',
        text: "Wednesday 11 AM — I'll send a performance digest with engagement rates, CTR, and the Threads experiment read. If anything's off-track, I'll surface it before the Friday post goes live so you can adjust.",
        delay: 1500,
        completes: true,
      },
    ],
  },

  t3: {
    agent: 'marsi',
    title: 'Review ad creative variations',
    steps: [
      { role: 'user', text: "Marsi, show me the ad creative variants for the LinkedIn Developer Series — what's your read on the mix?" },
      {
        role: 'agent',
        text: "Priya — I've got 4 creative variations for the LinkedIn Developer Series ready for your call. Wright drafted copy, Neha did the visuals. Budget is $3,200 across the 4-week campaign.",
        thinking: 'Loading creative variants + performance projections...',
        delay: 2200,
      },
      { role: 'user', text: 'Walk me through them.' },
      {
        role: 'agent',
        text: "**Variant A (Safe):** product screenshot + \"Build workflows in minutes\" headline. Conversion-focused. Projected CTR 2.4%.\n\n**Variant B (Thesis):** quote card from Wright's blog — \"Automate right, not most.\" No product shot. Projected CTR 3.1%.\n\n**Variant C (Customer):** real Acme screenshot + founder quote. Trust play. Projected CTR 3.6%.\n\n**Variant D (Bold):** provocative hook — \"Your workflow tool is slowing you down.\" Projected CTR 3.8%, but higher bounce risk.\n\nMy recommendation: run B + C as primary, D as a test pool at 15% budget. Skip A — it's the safe option we've tested six times already.",
        thinking: 'Running historical performance comparisons...',
        delay: 2500,
      },
      { role: 'user', text: 'Why skip A? That\'s usually our baseline.' },
      {
        role: 'agent',
        text: "Because for this audience it's not a baseline anymore — it's a ceiling. VP+ at dev tool companies are saturated with product-screenshot ads. We've run A-pattern creatives 6 times in the last year. Best CTR was 2.4%. Worst was 1.8%. It's the predictable middle.\n\nB and C take real positions — they either work or teach us something. A just gives us a number we already know.",
        thinking: 'Pulling 12-month creative performance history...',
        delay: 2300,
      },
      { role: 'user', text: 'Good call. B + C primary, D test pool. What\'s the cutoff for D?' },
      {
        role: 'agent',
        text: "72-hour cutoff on D. If CTR is below 2.5% or bounce rate above 75% after 48 hours with 20K impressions, I'll pause it automatically and reallocate to whichever of B/C is leading. You don't need to make that call manually — I'll flag you the moment it triggers.",
        thinking: 'Setting auto-pause thresholds...',
        delay: 2100,
      },
      { role: 'user', text: 'Perfect. One more thing — Raj from the agency sent a report on Meta, should I be worried about this campaign tracking the same way?' },
      {
        role: 'agent',
        text: "Different animal. Meta campaign is failing because of audience targeting drift — we're too broad. This LinkedIn series is tight: VP+ at dev tool companies, 50-200 employees. The targeting is the strongest part of the plan.\n\nThat said, I'll set the same weekly review cadence Raj suggested for Meta so we catch drift early. Watch, don't worry.",
        thinking: 'Cross-referencing campaign setup...',
        delay: 2200,
      },
      { role: 'user', text: 'Launch it.' },
      {
        role: 'agent',
        text: "Launching. B + C at 85% budget split, D at 15% with 72-hour auto-pause. Full campaign live in 30 minutes. I'll send you the first 24-hour performance read at 9 AM tomorrow.",
        delay: 1500,
        completes: true,
      },
    ],
  },

  t4: {
    agent: 'marsi',
    title: 'Check Meta campaign metrics',
    steps: [
      { role: 'user', text: "Marsi, 12 PM check — where are we on the Meta campaign? Raj's 2 PM deadline is looming." },
      {
        role: 'agent',
        text: "Priya — 12 PM metrics check on the Meta Brand Awareness campaign. Honest summary: it's not working, and I want your sign-off on the pause + reallocation before Raj's 2 PM deadline.",
        thinking: 'Pulling live campaign metrics...',
        delay: 2200,
      },
      { role: 'user', text: 'What\'s the state of play?' },
      {
        role: 'agent',
        text: "**5-day snapshot:**\n• Impressions: 28,400 (on target)\n• CTR: 1.9% (target: 2.8%) — **this is the problem**\n• Spend: $2,100 of $5,000 approved\n• Leads: 12 (projected 25 — on pace for ~17)\n\nCTR is the canary. Broad SMB audience is attracting non-decision-makers. Ad Set B in particular is bleeding — 1.1% CTR alone.",
        thinking: 'Analyzing audience performance by segment...',
        delay: 2500,
      },
      { role: 'user', text: 'Raj recommended pausing Ad Set B. Do you agree?' },
      {
        role: 'agent',
        text: "Agreed, with one addition. Raj's recommendation is to pause B and reallocate $800 to the top-performing LinkedIn creative (3.4% CTR). That's right directionally, but I'd go further:\n\n• Pause Ad Set B immediately — it's cratering the blended CTR\n• Narrow Ad Set A targeting to VP+ only, drop SMB generic\n• Reallocate the full $800 to LinkedIn, where it actually converts\n\nNet effect: spend efficiency up ~40%, lead quality up significantly.",
        thinking: 'Modeling reallocation outcomes...',
        delay: 2400,
      },
      { role: 'user', text: 'What\'s the risk of narrowing A rather than just pausing B?' },
      {
        role: 'agent',
        text: "Small. Ad Set A is our lowest-performing active audience at 2.1% CTR. Narrowing to VP+ cuts reach but should lift CTR to 3%+ based on our historical VP+ performance. We give up impressions, gain lead quality.\n\nDownside case: CTR doesn't lift and we lose another $300. I'll set a 48-hour checkpoint — if it's not moving, I pause A too and move fully to LinkedIn.",
        thinking: 'Running VP+ narrowing scenarios...',
        delay: 2300,
      },
      { role: 'user', text: 'Execute. Pause B, narrow A, reallocate $800 to LinkedIn. 48-hour checkpoint on A.' },
      {
        role: 'agent',
        text: "Executing now.\n\n• Ad Set B paused\n• Ad Set A targeting narrowed to VP+ only\n• $800 reallocated to LinkedIn top performer\n• 48-hour auto-checkpoint set on Ad Set A\n\nRaj gets the change log by 1 PM so he's aligned before his 2 PM deadline.",
        thinking: 'Pushing changes to Meta Ads + notifying Raj...',
        delay: 2000,
      },
      { role: 'user', text: 'Good. Let me know how Ad Set A performs.' },
      {
        role: 'agent',
        text: "I'll surface the 48-hour read Wednesday AM. If A recovers above 3% CTR, we hold. If not, I auto-pause and we have a bigger conversation about whether Meta is worth keeping as a channel at all.",
        delay: 1500,
        completes: true,
      },
    ],
  },

  /* ═══════════════════════════════════════════════════════════════
     DAVID'S TASKS — Sales-led multi-turn flows (st1-st5)
     ═══════════════════════════════════════════════════════════════ */
  st1: {
    agent: 'sally',
    title: 'Prep for NovaTech closing call',
    steps: [
      { role: 'user', text: "Sally, NovaTech closing call is at 11. Walk me through what I need to have locked before I get on the line." },
      {
        role: 'agent',
        text: "David — deal brief is ready. Honest summary: this is a 92% close, but there are two redlines and one competitive rumor I want you walking in with. Want me to go structured or narrative?",
        thinking: 'Pulling deal brief + redline responses...',
        delay: 2200,
      },
      { role: 'user', text: "Structured. Redlines first." },
      {
        role: 'agent',
        text: "**Two redlines on the table:**\n\n**Clause 4.2 (data retention):** Raj's legal wants 90 days vs our standard 180. Low-cost concede — we've done it 4 of last 5 enterprise deals with zero impact. Recommend: concede.\n\n**Clause 7.1 (liability cap):** Standard boilerplate, holds across 40+ past contracts. Recommend: hold, offer a 15-min legal-to-legal walkthrough instead of a redline.\n\nBoth responses are pre-drafted. You can execute on the call or send in writing after.",
        thinking: 'Cross-referencing redline precedents...',
        delay: 2500,
      },
      { role: 'user', text: "Competitive rumor?" },
      {
        role: 'agent',
        text: "Soft one. I picked up LinkedIn activity — Raj's VP of Engineering attended an AgentHub webinar last week. Not a red flag on its own (engineering VPs attend lots of webinars), but worth having a confident answer if it comes up.\n\nRecommended framing: *'We're aware there are other tools in your eval — we'd rather win on the 45-day implementation than the deck. If you want to do a head-to-head on API integration speed, happy to set that up.'* Puts us on concrete ground, not sales talk.",
        thinking: 'Scanning LinkedIn + competitor signal...',
        delay: 2300,
      },
      { role: 'user', text: "Good framing. What's my close approach?" },
      {
        role: 'agent',
        text: "**Direct + ROI-anchored.** Raj responds to specifics, not rapport. The sequence I'd recommend:\n\n1. Open with the redline resolution (shows preparedness)\n2. Walk through implementation timeline (Week 1 onboarding, Week 3 first workflow, Week 6 production)\n3. Anchor ROI — reference the Acme Corp 40% onboarding reduction\n4. Close with: *'Do you see anything between us and a Thursday sign-off?'*\n\nThat close forces him to either confirm or surface the real blocker. No ambiguity.",
        thinking: "Mapping against Raj's previous negotiation patterns...",
        delay: 2400,
      },
      { role: 'user', text: "Solid. One more — if he pushes on pricing for more seats later, what's my floor?" },
      {
        role: 'agent',
        text: "Current deal is 45 seats at $28K. If he asks about expansion to 80 seats (which I'd expect in Q2), your floor is $47K — that's the 15% volume discount point and preserves margin. Below that, loop me in before committing.\n\nHappy to prep a 3-tier expansion sheet right now if you want it in your back pocket for the call.",
        thinking: 'Modeling expansion pricing scenarios...',
        delay: 2200,
      },
      { role: 'user', text: "Yes, prep the expansion sheet. Send it to me before 11." },
      {
        role: 'agent',
        text: "On it. Expansion sheet in your inbox by 10:45, includes: 45/60/80/100-seat pricing, volume discount tiers, and a 2-line talking script for each tier. You'll have it ready if Raj brings up Q2.",
        delay: 1500,
        completes: true,
      },
    ],
  },

  st2: {
    agent: 'sally',
    title: 'Review updated Q1 forecast',
    steps: [
      { role: 'user', text: "Sally, walk me through the Q1 forecast revision. What changed, and what's your confidence?" },
      {
        role: 'agent',
        text: "David — Q1 went from $380K to $342K. Two enterprise slips, partial SMB offset. Net gap is $16K on plan. Finn and I have jointly modeled the path back. Want the narrative or the numbers?",
        thinking: 'Loading Q1 forecast + Finn cashflow model...',
        delay: 2200,
      },
      { role: 'user', text: "Both. Numbers first." },
      {
        role: 'agent',
        text: "**Revised forecast: $342K**\n\n**Slippages:**\n• TechCorp — $52K ARR, champion silent 12 days\n• DataFlow — $38K ARR, budget freeze until March\n• Combined slip: $90K\n\n**SMB offset:**\n• Late-stage pipeline strength: +22% above Q1 target\n• Three deals representing ~$80K (NovaTech $28K, Meridian $24K projected, CloudNine $28K)\n• Net of slippage + offset: $342K vs $380K plan\n\n**Net plan gap:** $38K original → $16K after SMB uplift. Real gap is small if SMB executes.",
        thinking: 'Cross-referencing CRM close probabilities...',
        delay: 2600,
      },
      { role: 'user', text: "What's your confidence on the three SMB deals?" },
      {
        role: 'agent',
        text: "Deal by deal:\n\n**NovaTech ($28K) — 92%.** Thursday close expected. Two minor redlines, no surprises, champion is bought in. Lowest execution risk.\n\n**Meridian Health ($24K projected) — 60%.** Strong demo signal, VP asking the right questions. But we don't have a quote out yet — every week without a quote drops probability 5-8 points. Need a Thursday working session to scope.\n\n**CloudNine ($28K) — 55%.** Mid-stage, positive but slow. Champion is a Head of DevEx, not a VP — so adoption signal is strong but budget path is longer. Expecting late Feb close, which means it's a Q1-edge deal at best.\n\n**Weighted close probability across all three:** $56K. Which means our real Q1 exit should be ~$320K-$340K. We're honest.",
        thinking: "Running per-deal stage/probability analysis...",
        delay: 2700,
      },
      { role: 'user', text: "Finn's take?" },
      {
        role: 'agent',
        text: "Finn ran three cashflow scenarios against this forecast:\n\n• **Low case ($320K):** Runway still holds at 6.2 months — above the board-mandated 6-month floor\n• **Mid case ($342K):** 6.8 months runway\n• **High case ($380K):** 7.4 months\n\nAll three keep us above the safety floor. Which is what Neel needs for his Wednesday board package. The story isn't 'we're at risk' — it's 'we're at plan minus $16K, with 6+ months runway in every scenario.'\n\nThat's a survivable headline.",
        thinking: 'Pulling Finn 3-scenario model...',
        delay: 2400,
      },
      { role: 'user', text: "Approved. What do you need from me?" },
      {
        role: 'agent',
        text: "Three things:\n\n1. **CEO outreach on TechCorp** — approve my request in the Needs Decision column. Changes probability materially.\n2. **Meridian Thursday session** — confirm your availability in my calendar pull so I can lock the slot.\n3. **CloudNine** — decide if you want to push them to close in Feb (requires a discount concession) or let them glide to March. Finn's view: glide unless you need the Q1 number.\n\nMark the forecast reviewed and I'll sync to Neel's model.",
        thinking: 'Preparing actions...',
        delay: 2200,
      },
      { role: 'user', text: "Reviewed. Glide CloudNine. I'll approve TechCorp outreach after this. Lock Meridian Thursday." },
      {
        role: 'agent',
        text: "Locked. Forecast marked reviewed, synced to Finn's model, and Neel has the update for his board package. Meridian Thursday session holding 2-2:45 PM. CloudNine set to glide — no pricing concession applied.\n\nI'll surface any change to the SMB probabilities in real time.",
        delay: 1500,
        completes: true,
      },
    ],
  },

  st3: {
    agent: 'sally',
    title: 'Follow up with Meridian Health',
    steps: [
      { role: 'user', text: "Sally, what's the state of play on Meridian? I want to send a clean follow-up today." },
      {
        role: 'agent',
        text: "David — Meridian is warm. Anita attended the full demo, asked implementation questions in real time (strong buying signal), and followed up the next morning with pricing and team-size questions. We're in the early-proposal stage. I've drafted both a follow-up email and a proposal outline for you.",
        thinking: 'Pulling demo notes + Anita email thread...',
        delay: 2200,
      },
      { role: 'user', text: "What's your read on the proposal outline?" },
      {
        role: 'agent',
        text: "**Structure:**\n\n• **Phased rollout** (ops team first, 45-day pilot, then company-wide) — matches the exact framing she used in her follow-up email\n• **3 workflow scopes** pulled from the demo (ticket triage, onboarding automation, compliance routing)\n• **Pricing: $24K/year for 85 seats** — that's the target number, anchored to their size\n• **Implementation timeline** — 2-hour/week internal point person, 45-day pilot, 8 weeks to full rollout\n• **Success metrics** — we commit to specific workflow-hours saved in the pilot window\n\nThe outline mirrors how she's already thinking. Which is the right framing — we're confirming her mental model, not selling against it.",
        thinking: 'Mapping proposal against Meridian stated needs...',
        delay: 2500,
      },
      { role: 'user', text: "Why the $24K number specifically? Not $26K?" },
      {
        role: 'agent',
        text: "Three reasons:\n\n1. **Anchor to size**, not to willingness to pay. 85 seats × our standard rate is ~$24K. $26K asks for a 'why' explanation she'll need to take to her CFO. $24K doesn't.\n\n2. **Leaves upside room.** If she expands to 120 seats next year, the price-per-seat logic stays consistent. We don't have to renegotiate the baseline.\n\n3. **Accelerates close.** Anita is a champion who brings a predictable number to her CFO. The cleaner the math, the faster the sign-off.\n\nPhilosophically — price discipline matters more than squeezing $2K out of the first contract. Meridian could be a 3-year, $100K+ customer.",
        thinking: 'Running historical anchor-pricing outcomes...',
        delay: 2400,
      },
      { role: 'user', text: "Good logic. What's the email framing — do we send this as a proposal, or open with a working session?" },
      {
        role: 'agent',
        text: "**Working session first.** My draft opens with a Thursday-or-Friday invite for a 45-min scoping session — you + a solutions engineer + her. We walk through her top 3 workflows live, then deliver the proposal against those specifics rather than a generic doc.\n\nWhy: it converts 2.4x better than sending a cold proposal. And Anita's the type who will want to feel we've heard her specifically before seeing a number.\n\n**If** she pushes for the proposal today without a session — I've got a backup version ready. But the working-session route is the A play.",
        thinking: 'Running proposal-vs-workshop conversion math...',
        delay: 2300,
      },
      { role: 'user', text: "Send the working session version. What time am I offering?" },
      {
        role: 'agent',
        text: "Thursday 2-2:45 PM or Friday 10-10:45 AM. Both slots have our strongest solutions engineer (Priya K. on the SE team) available. I'll CC her into the thread the moment Anita picks.\n\nSending now — want me to flag it as high-priority in your sent folder so you can track the reply?",
        thinking: 'Checking calendar conflicts...',
        delay: 2000,
      },
      { role: 'user', text: "Yes, flag it. And if she doesn't reply by EOD Tuesday, nudge her once." },
      {
        role: 'agent',
        text: "Sent and flagged. Nudge queued for Tuesday 4 PM if no reply. I'll also drop a quick ping into your briefing tomorrow morning with her reply status so you're not chasing it.",
        delay: 1500,
        completes: true,
      },
    ],
  },

  st4: {
    agent: 'sally',
    title: 'CRM hygiene — 8 stale opportunities',
    steps: [
      { role: 'user', text: "Sally, walk me through the 8 stale deals. I want to know what to kill vs what to revive." },
      {
        role: 'agent',
        text: "David — 8 opportunities with 30+ days of zero activity. I've classified each into three buckets: revive, archive, or handoff. Want to go one-by-one or by bucket?",
        thinking: 'Pulling CRM stale-deal list + activity history...',
        delay: 2200,
      },
      { role: 'user', text: "By bucket. Start with archive — fastest decisions." },
      {
        role: 'agent',
        text: "**Archive — 4 deals, zero champion response in 45+ days:**\n\n• **ByteForge** ($18K, last contact 52 days) — champion left the company per LinkedIn\n• **Orbital Labs** ($22K, 48 days) — raised Series A, pivoted away from our category\n• **Nimbus Data** ($14K, 62 days) — small team, bought a competitor's free tier\n• **Pixel Tree** ($9K, 41 days) — no size signal, unclear fit\n\nRecommend archiving all 4. Combined pipeline impact: $63K removed. None were closing anyway — they're just inflating our numbers.",
        thinking: 'Cross-checking LinkedIn + funding signals...',
        delay: 2500,
      },
      { role: 'user', text: "Approved — archive all 4. Revive bucket?" },
      {
        role: 'agent',
        text: "**Revive — 2 deals, signal came back recently:**\n\n• **StackSpark** ($32K, 35 days silent) — champion liked our December blog post on LinkedIn last week. Real signal. Recommend a thesis-led re-engagement (Wright has the content).\n\n• **LoopGrid** ($45K, 31 days) — their new VP of Platform joined from a customer of ours (Acme Corp). Warm intro path. Recommend a soft intro email this week.\n\nBoth worth a touch. Combined upside if even one reopens: $45K-$77K re-entering the pipeline.",
        thinking: 'Scanning LinkedIn engagement + org changes...',
        delay: 2400,
      },
      { role: 'user', text: "Do both. StackSpark gets the thesis email, LoopGrid gets the intro ask. Who handles?" },
      {
        role: 'agent',
        text: "I'll own both. StackSpark re-engagement goes out today — draft is ready, Wright-thesis framing, 2-sentence ask. LoopGrid intro ask routes through Acme — I'll ping Lisa Park for a warm intro path.\n\nLast bucket — handoff. Want it?",
        thinking: 'Queuing outreach sequences...',
        delay: 2200,
      },
      { role: 'user', text: "Yes, handoff bucket." },
      {
        role: 'agent',
        text: "**Handoff — 2 deals, better suited for another channel:**\n\n• **Verdant Tools** ($8K, 38 days) — SMB that fits our self-serve tier. Recommend moving to Priya's product-led motion — Marsi can run a re-engagement sequence at $0 cost per touch.\n\n• **Glint Analytics** ($16K, 39 days) — mid-market but they need a 6-month procurement cycle. Move to Q3 pipeline, let Finn surface it in the mid-year review. Not urgent.\n\nCombined pipeline reclassified: $24K moves out of 'sales-managed' into proper homes.",
        thinking: 'Matching deals against motion fit...',
        delay: 2300,
      },
      { role: 'user', text: "Approved all. Execute the CRM moves." },
      {
        role: 'agent',
        text: "Executing. 4 deals archived, 2 routed to re-engagement with me as owner, 2 handed off (Verdant → Priya's PLG motion, Glint → Finn's Q3 queue). Net CRM health impact: pipeline quality score up from 62 to 81. Dashboard refresh in 10 minutes.",
        delay: 1500,
        completes: true,
      },
    ],
  },

  st5: {
    agent: 'dora',
    title: 'Review team coaching notes',
    steps: [
      { role: 'user', text: "Dora, what are the patterns from the sales calls this week? Where do the reps need help?" },
      {
        role: 'agent',
        text: "David — I've analyzed 12 rep conversations from the last 5 days. Three patterns worth flagging. Two are fixable with coaching, one is a structural issue in the script. Want the patterns or the specific call examples first?",
        thinking: 'Analyzing 12 call recordings + transcripts...',
        delay: 2200,
      },
      { role: 'user', text: "Patterns first." },
      {
        role: 'agent',
        text: "**Pattern 1 — Discovery cut short (6 of 12 calls).**\nReps are moving to product demo inside 7 minutes of call start. Average time-to-demo for our top performers is 14 minutes. Cutting discovery correlates with a 34% drop in win rate. Fixable with coaching.\n\n**Pattern 2 — ROI framing skipped (8 of 12 calls).**\nReps are quoting seat price but not anchoring ROI. When we don't anchor ROI, 'expensive' objections go up 2.1x. Fixable with a 5-min script update.\n\n**Pattern 3 — Objection avoidance on pricing (4 of 12 calls).**\nWhen the prospect pushes on price, reps pivot to features instead of holding the price with a value story. This is a confidence pattern, not a knowledge gap. Requires 1:1 coaching, not script.",
        thinking: 'Running conversation pattern classification...',
        delay: 2600,
      },
      { role: 'user', text: "Who's cutting discovery? Specific reps." },
      {
        role: 'agent',
        text: "Three repeat offenders:\n\n• **Raj M.** — 4 of his 5 calls this week. Pattern: moves to demo the moment the prospect says 'show me the product.' Needs coaching on 'earn the demo.'\n\n• **Priya N.** — 2 of her 3 calls. Pattern: opens with discovery but abandons it at first technical question. Needs coaching on structured discovery frameworks.\n\n• **Jamal F.** — 1 of 4 calls. Lower frequency, less concerning. Probably situational, not systemic.\n\nRecommend a 30-min team session on Monday covering 'earning the demo' + the ROI anchoring script update. I can prepare the deck with the specific call moments anonymized.",
        thinking: 'Indexing calls by rep + pattern...',
        delay: 2500,
      },
      { role: 'user', text: "Yes, prep the team deck. What about the pricing-objection coaching — who needs it most?" },
      {
        role: 'agent',
        text: "Priya N. and Raj M. — same two. The pattern is consistent: both come from technical-buyer backgrounds, both default to feature-answer when price comes up. That's a confidence reframe, not a script update.\n\nRecommendation: pair each with a top performer (Lena H. for Priya, Marcus D. for Raj) for a 30-min live coaching call next week. I'll set up the pairings and send a pre-read on the specific moments to work through.\n\nCompound this with the team session and you'll see win-rate uplift in 2-3 weeks.",
        thinking: 'Matching coaches to rep patterns...',
        delay: 2400,
      },
      { role: 'user', text: "Set it up. When does the team session go on the calendar?" },
      {
        role: 'agent',
        text: "Monday 10-10:30 AM. 30 min, optional for senior reps, required for the three named. Pairings for the 1:1 coaching calls will drop in rep calendars Tuesday. I'll also publish the deck to your shared drive so you can review before Monday — plan on a 10-min pre-read.",
        thinking: 'Scheduling sessions...',
        delay: 2200,
      },
      { role: 'user', text: "Great. Send me the deck by EOD Friday." },
      {
        role: 'agent',
        text: "Deck in your inbox by Friday 4 PM. Will include: anonymized call moments for all three patterns, the revised ROI anchoring script, and the coaching pairings. Session locked Monday 10 AM.",
        delay: 1500,
        completes: true,
      },
    ],
  },
};
